import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { config } from '@/config';
import { applyPendingDocsBlinkSync, clearPendingDocsBlinkSync } from '@/utils/pendingDocsBlinkSync';

const API = config.BACKEND_URL;

const GroupContext = createContext(null);

export function GroupProvider({ children }) {
  const { user, token } = useAuth();

  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [unreadNewsCount, setUnreadNewsCount] = useState(0);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const [newsLoaded, setNewsLoaded] = useState(false);

  const authHdrs = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const refreshPendingDocuments = useCallback(async () => {
    if (!user?.id || !token) { setPendingDocuments([]); setDocumentsLoaded(true); return; }
    try {
      const r = await fetch(`${API}/group/documents/pending`, { headers: authHdrs() });
      if (r.ok) setPendingDocuments(await r.json());
    } catch {
      // silencieux
    } finally {
      setDocumentsLoaded(true);
    }
  }, [user?.id, token, authHdrs]);

  const refreshUnreadNews = useCallback(async () => {
    if (!user?.id || !token) { setUnreadNewsCount(0); setNewsLoaded(true); return; }
    try {
      const r = await fetch(`${API}/group/news/unread-count`, { headers: authHdrs() });
      if (r.ok) {
        const d = await r.json();
        setUnreadNewsCount(d.count ?? 0);
      }
    } catch {
      // silencieux
    } finally {
      setNewsLoaded(true);
    }
  }, [user?.id, token, authHdrs]);

  const markDocumentRead = useCallback(async (documentId, accepte = false) => {
    if (!token) return;
    await fetch(`${API}/group/documents/${documentId}/read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHdrs() },
      body: JSON.stringify({ accepte }),
    });
    await refreshPendingDocuments();
  }, [token, authHdrs, refreshPendingDocuments]);

  const markNewsRead = useCallback(async (newsId) => {
    if (!token) return;
    await fetch(`${API}/group/news/${newsId}/read`, {
      method: 'POST',
      headers: authHdrs(),
    });
    setUnreadNewsCount(prev => Math.max(0, prev - 1));
  }, [token, authHdrs]);

  const markAllVisibleNewsRead = useCallback(async (newsIds) => {
    if (!token || !newsIds?.length) return;
    await Promise.all(newsIds.map(id =>
      fetch(`${API}/group/news/${id}/read`, { method: 'POST', headers: authHdrs() })
    ));
    await refreshUnreadNews();
  }, [token, authHdrs, refreshUnreadNews]);

  useEffect(() => {
    if (user?.id) {
      refreshPendingDocuments();
      refreshUnreadNews();
    } else {
      setPendingDocuments([]);
      setUnreadNewsCount(0);
      setDocumentsLoaded(false);
      setNewsLoaded(false);
    }
  }, [user?.id, refreshPendingDocuments, refreshUnreadNews]);

  const hasBlockingPendingDocuments = pendingDocuments.some(
    (d) => d.lu_obligatoire && d.bloque_prysmapp_si_non_signe !== false
  );

  // Après signature dans Access (autre onglet) : poll + refresh au retour sur Prysm'app
  useEffect(() => {
    if (!user?.id || !token) return undefined;

    const onVisibleOrFocus = () => {
      if (document.visibilityState === 'visible') {
        refreshPendingDocuments();
      }
    };
    window.addEventListener('focus', onVisibleOrFocus);
    document.addEventListener('visibilitychange', onVisibleOrFocus);

    // Poll tant qu'il reste des docs à signer (déblocage sans F5)
    let intervalId = null;
    if (pendingDocuments.length > 0) {
      intervalId = window.setInterval(() => {
        refreshPendingDocuments();
      }, 4000);
    }

    return () => {
      window.removeEventListener('focus', onVisibleOrFocus);
      document.removeEventListener('visibilitychange', onVisibleOrFocus);
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [user?.id, token, pendingDocuments.length, refreshPendingDocuments]);

  const hadPendingDocsRef = useRef(false);
  const hadBlockingRef = useRef(false);
  useEffect(() => {
    const hasPending = pendingDocuments.length > 0;
    if (hasPending && !hadPendingDocsRef.current) {
      applyPendingDocsBlinkSync();
    } else if (!hasPending && hadPendingDocsRef.current) {
      clearPendingDocsBlinkSync();
    }
    hadPendingDocsRef.current = hasPending;

    if (hadBlockingRef.current && !hasBlockingPendingDocuments) {
      toast.success("Documents signés — applications débloquées");
    }
    hadBlockingRef.current = hasBlockingPendingDocuments;
  }, [pendingDocuments.length, hasBlockingPendingDocuments]);

  return (
    <GroupContext.Provider value={{
      pendingDocuments,
      hasBlockingPendingDocuments,
      unreadNewsCount,
      documentsLoaded,
      newsLoaded,
      refreshPendingDocuments,
      refreshUnreadNews,
      markDocumentRead,
      markNewsRead,
      markAllVisibleNewsRead,
    }}>
      {children}
    </GroupContext.Provider>
  );
}

export function useGroup() {
  const ctx = useContext(GroupContext);
  if (!ctx) throw new Error('useGroup must be used inside GroupProvider');
  return ctx;
}
